/**
 * Cavalry Renderer - Specialized renderer for cavalry units with galloping animation
 * Features:
 * - Procedural galloping animation via custom shaders
 * - Horse body bobbing, leg cycling, mane/tail flowing
 * - Rider bouncing, lance swaying
 * - Dust trail particles when moving
 * - Lance charge attack animation
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';

const MAX_CAVALRY = 300;
const MAX_DUST_PARTICLES = 2000;

// Animation group IDs encoded in vertex attribute
const ANIM_GROUP = {
  STATIC: 0, // No animation (saddle, stirrups)
  BODY: 1, // Horse body - vertical bob
  HEAD: 2, // Horse head - slight nod
  FRONT_LEFT_LEG: 3,
  FRONT_RIGHT_LEG: 4,
  BACK_LEFT_LEG: 5,
  BACK_RIGHT_LEG: 6,
  MANE: 7, // Mane - wave motion
  TAIL: 8, // Tail - wave motion
  RIDER_BODY: 9, // Rider - bounce with horse
  RIDER_HEAD: 10,
  LANCE: 11, // Lance - slight sway
  CAPE: 12, // Cape - flutter
};

// Color palette
const COLORS = {
  skin: new THREE.Color(0xf5deb3),
  gold: new THREE.Color(0xdaa520),
  bronze: new THREE.Color(0xcd7f32),
  steel: new THREE.Color(0xa8a8a8),
  cavalryArmor: new THREE.Color(0x5a6a7a), // Blue-gray armor
  cavalryAccent: new THREE.Color(0x8b7355), // Brown leather
  tassel: new THREE.Color(0xcc2222),
  hair: new THREE.Color(0x1a1a1a),
  wood: new THREE.Color(0x8b4513),
  woodDark: new THREE.Color(0x5d4037),
  // Horse colors - more realistic
  horseBay: new THREE.Color(0x8b4513), // Bay brown
  horseChestnut: new THREE.Color(0xcd853f), // Chestnut
  horseDark: new THREE.Color(0x3d2817), // Dark brown
  horseMuzzle: new THREE.Color(0x2d1f10), // Dark muzzle
  mane: new THREE.Color(0x1a1208), // Nearly black mane
  hoof: new THREE.Color(0x2d2d2d), // Dark gray hoof
  // Marker colors for faction tinting
  markerPrimary: new THREE.Color(1.0, 0.1, 0.1),
  markerSecondary: new THREE.Color(0.1, 1.0, 0.1),
};

// Faction colors
const FACTION_COLORS: Record<string, { primary: THREE.Color; secondary: THREE.Color }> = {
  yan: { primary: new THREE.Color(0xc73e3a), secondary: new THREE.Color(0xd4a843) },
  han: { primary: new THREE.Color(0x5b9bd5), secondary: new THREE.Color(0xe8e8e8) },
  lin: { primary: new THREE.Color(0x4caf50), secondary: new THREE.Color(0x795548) },
  shang: { primary: new THREE.Color(0xffd700), secondary: new THREE.Color(0x7b1fa2) },
  xuan: { primary: new THREE.Color(0x4a148c), secondary: new THREE.Color(0x212121) },
  tie: { primary: new THREE.Color(0x607d8b), secondary: new THREE.Color(0xff5722) },
  yun: { primary: new THREE.Color(0x03a9f4), secondary: new THREE.Color(0xeceff1) },
  neutral: { primary: new THREE.Color(0x888888), secondary: new THREE.Color(0xaaaaaa) },
};

interface CavalryData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number; // Movement speed for animation intensity
  animPhase: number; // Animation phase offset
  attackTime: number; // Time when attack started (-1 = not attacking)
  attackDuration: number; // Duration of attack animation
}

// Vertex shader with galloping animation and charge attack
const cavalryVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;
  uniform float uGlobalSpeed;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  // Animation parameters
  const float PI = 3.14159265359;
  const float GALLOP_FREQUENCY = 6.0; // Cycles per second at full speed
  const float ATTACK_DURATION = 0.65; // Attack animation duration in seconds

  // Rotation helper - rotate around Y axis
  vec3 rotateY(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x * c + p.z * s,
      p.y,
      -p.x * s + p.z * c
    ) + pivot;
  }

  // Rotation helper - rotate around Z axis
  vec3 rotateZ(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x * c - p.y * s,
      p.x * s + p.y * c,
      p.z
    ) + pivot;
  }

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime) {
    vec3 animated = pos;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases:
        // 0-0.15 = prepare (lower lance)
        // 0.15-0.4 = charge thrust
        // 0.4-0.55 = impact hold
        // 0.55-1.0 = recover
        if (attackProgress < 0.15) {
          attackPhase = attackProgress / 0.15;  // 0-1 prepare
        } else if (attackProgress < 0.4) {
          attackPhase = 1.0 + (attackProgress - 0.15) / 0.25;  // 1-2 thrust
        } else if (attackProgress < 0.55) {
          attackPhase = 2.0 + (attackProgress - 0.4) / 0.15;  // 2-3 impact
        } else {
          attackPhase = 3.0 + (attackProgress - 0.55) / 0.45;  // 3-4 recover
        }
      }
    }

    // During attack, boost gallop intensity
    float attackBoost = attackProgress > 0.0 ? 0.3 : 0.0;

    // Scale animation by movement speed (0 = idle, 1 = full gallop)
    float intensity = clamp(speed + attackBoost, 0.0, 1.0);
    float t = time * GALLOP_FREQUENCY + phase;

    // Base body bob - applied to ALL parts except static and legs
    float baseBob = sin(t * 2.0) * 0.004 * intensity;
    float idleBob = sin(time * 2.0 + phase) * 0.001 * (1.0 - intensity);

    // Attack-based forward lunge
    float attackLunge = 0.0;
    if (attackPhase > 0.0) {
      if (attackPhase < 1.0) {
        attackLunge = 0.01 * attackPhase;
      } else if (attackPhase < 2.0) {
        float thrustPhase = attackPhase - 1.0;
        attackLunge = 0.01 + 0.03 * thrustPhase;
      } else if (attackPhase < 3.0) {
        attackLunge = 0.04;
      } else {
        float recoverPhase = attackPhase - 3.0;
        attackLunge = 0.04 * (1.0 - recoverPhase);
      }
    }

    if (group < 0.5) {
      // STATIC - no animation (stirrups, etc.)
      return animated;
    }

    // Apply base bob to all animated parts
    animated.y += baseBob + idleBob;
    animated.x += attackLunge;

    if (group < 1.5) {
      // BODY - just the base bob (already applied)
      // During charge, horse stretches forward
      if (attackPhase >= 1.0 && attackPhase < 3.0) {
        float stretch = (attackPhase < 2.0) ? (attackPhase - 1.0) : 1.0;
        animated.x += stretch * 0.008;
      }
    }
    else if (group < 2.5) {
      // HEAD - slight additional nod
      float nod = sin(t * 2.0 + PI * 0.3) * 0.003 * intensity;
      animated.y += nod * 0.3;
      animated.x += nod * 0.5;

      // During attack, head lowers aggressively
      if (attackPhase > 0.0) {
        float headLower = 0.0;
        if (attackPhase < 1.0) {
          headLower = attackPhase * 0.008;
        } else if (attackPhase < 3.0) {
          headLower = 0.008;
        } else {
          headLower = 0.008 * (1.0 - (attackPhase - 3.0));
        }
        animated.y -= headLower;
        animated.x += headLower * 0.5;
      }
    }
    else if (group < 3.5) {
      // FRONT_LEFT_LEG - faster during attack
      float legSpeed = attackPhase > 0.0 ? 1.5 : 1.0;
      float cycle = t * legSpeed;
      float lift = max(sin(cycle), 0.0) * 0.012 * intensity;
      float swing = sin(cycle) * 0.008 * intensity;
      animated.y += lift;
      animated.x += swing;
    }
    else if (group < 4.5) {
      // FRONT_RIGHT_LEG
      float legSpeed = attackPhase > 0.0 ? 1.5 : 1.0;
      float cycle = t * legSpeed + PI;
      float lift = max(sin(cycle), 0.0) * 0.012 * intensity;
      float swing = sin(cycle) * 0.008 * intensity;
      animated.y += lift;
      animated.x += swing;
    }
    else if (group < 5.5) {
      // BACK_LEFT_LEG
      float legSpeed = attackPhase > 0.0 ? 1.5 : 1.0;
      float cycle = t * legSpeed + PI * 0.5;
      float lift = max(sin(cycle), 0.0) * 0.010 * intensity;
      float swing = sin(cycle) * 0.006 * intensity;
      animated.y += lift;
      animated.x += swing;
    }
    else if (group < 6.5) {
      // BACK_RIGHT_LEG
      float legSpeed = attackPhase > 0.0 ? 1.5 : 1.0;
      float cycle = t * legSpeed + PI * 1.5;
      float lift = max(sin(cycle), 0.0) * 0.010 * intensity;
      float swing = sin(cycle) * 0.006 * intensity;
      animated.y += lift;
      animated.x += swing;
    }
    else if (group < 7.5) {
      // MANE - wave motion, more violent during attack
      float maneIntensity = intensity * (1.0 + attackPhase * 0.3);
      float wave = sin(t * 3.0 + pos.x * 15.0) * 0.004 * maneIntensity;
      animated.z += wave;
      animated.x -= abs(wave) * 0.3;
      // Idle flutter
      animated.z += sin(time * 3.0 + pos.x * 10.0) * 0.001 * (1.0 - intensity);
    }
    else if (group < 8.5) {
      // TAIL - streams back during charge
      float tailIntensity = intensity * (1.0 + attackPhase * 0.5);
      float wave = sin(t * 2.5) * 0.006 * tailIntensity;
      animated.z += wave;
      // During attack, tail streams more backward
      if (attackPhase > 0.0) {
        animated.x -= 0.01 * min(attackPhase, 1.0);
      }
      // Idle tail swish
      animated.z += sin(time * 2.0 + phase) * 0.002 * (1.0 - intensity);
    }
    else if (group < 9.5) {
      // RIDER_BODY - leans forward during attack
      float sway = sin(t * 1.0) * 0.001 * intensity;
      animated.z += sway;

      // During attack, rider leans forward aggressively
      if (attackPhase > 0.0) {
        vec3 riderPivot = vec3(0.0, 0.38, 0.0);
        float leanAngle = 0.0;
        if (attackPhase < 1.0) {
          leanAngle = attackPhase * 0.15;
        } else if (attackPhase < 2.0) {
          leanAngle = 0.15 + (attackPhase - 1.0) * 0.1;
        } else if (attackPhase < 3.0) {
          leanAngle = 0.25;
        } else {
          leanAngle = 0.25 * (1.0 - (attackPhase - 3.0));
        }
        animated = rotateZ(animated, leanAngle, riderPivot);
      }
    }
    else if (group < 10.5) {
      // RIDER_HEAD - follows body, focuses forward during attack
      float nod = sin(t * 2.0) * 0.001 * intensity;
      animated.x += nod * 0.3;

      // During attack, head leans forward with body
      if (attackPhase > 0.0) {
        vec3 headPivot = vec3(0.0, 0.60, 0.0);
        float leanAngle = 0.0;
        if (attackPhase < 1.0) {
          leanAngle = attackPhase * 0.12;
        } else if (attackPhase < 3.0) {
          leanAngle = 0.12;
        } else {
          leanAngle = 0.12 * (1.0 - (attackPhase - 3.0));
        }
        animated = rotateZ(animated, leanAngle, headPivot);
      }
    }
    else if (group < 11.5) {
      // LANCE - MAJOR attack animation: lowers to horizontal during charge
      float sway = sin(t * 1.5 + PI * 0.3) * 0.003 * intensity;
      animated.z += sway;

      // During attack, lance lowers dramatically
      if (attackPhase > 0.0) {
        // Lance pivot point (where rider holds it)
        vec3 lancePivot = vec3(0.10, 0.58, 0.0);

        float lowerAngle = 0.0;
        float thrustExtend = 0.0;

        if (attackPhase < 1.0) {
          // Prepare - lower lance to horizontal
          lowerAngle = attackPhase * 0.35;  // ~20 degrees
        } else if (attackPhase < 2.0) {
          // Thrust - fully horizontal with forward extension
          float thrustPhase = attackPhase - 1.0;
          lowerAngle = 0.35 + thrustPhase * 0.15;  // Continue lowering
          thrustExtend = thrustPhase * 0.06;
        } else if (attackPhase < 3.0) {
          // Impact - slight recoil
          float impactPhase = attackPhase - 2.0;
          lowerAngle = 0.5 - impactPhase * 0.05;
          thrustExtend = 0.06 - impactPhase * 0.02;
          // Add impact vibration
          float vibration = sin(impactPhase * 40.0) * 0.003 * (1.0 - impactPhase);
          animated.y += vibration;
          animated.z += vibration * 0.5;
        } else {
          // Recover - raise lance back
          float recoverPhase = attackPhase - 3.0;
          lowerAngle = 0.45 * (1.0 - recoverPhase);
          thrustExtend = 0.04 * (1.0 - recoverPhase);
        }

        // Apply lance rotation
        animated = rotateZ(animated, lowerAngle, lancePivot);
        animated.x += thrustExtend;
      }
    }
    else if (group < 12.5) {
      // CAPE - flutter more during attack
      float capeIntensity = intensity * (1.0 + attackPhase * 0.5);
      float flutter = sin(t * 4.0 + pos.y * 6.0) * 0.005 * capeIntensity;
      animated.x -= abs(flutter);
      animated.z += flutter * 0.5;

      // During attack, cape streams back
      if (attackPhase > 0.0) {
        animated.x -= 0.015 * min(attackPhase, 1.0);
      }
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime);

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Dust particle vertex shader
const dustVertexShader = `
  attribute float size;
  attribute float alpha;
  attribute vec3 velocity;

  uniform float uTime;

  varying float vAlpha;

  void main() {
    vAlpha = alpha;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Dust particle fragment shader
const dustFragmentShader = `
  varying float vAlpha;

  void main() {
    // Circular particle with soft edge
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;

    float alpha = vAlpha * (1.0 - dist * 2.0);
    vec3 dustColor = vec3(0.75, 0.68, 0.55); // Earthy dust color

    gl_FragColor = vec4(dustColor, alpha * 0.6);
  }
`;

// Fragment shader with faction colors and enhanced lighting
const cavalryFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;
  uniform float uTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vec3 finalColor = vColor;

    // Faction color replacement
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Balanced lighting - softer shadows
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    // Diffuse with wrap lighting for softer shadows
    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0); // Wrap lighting for fill

    // Subtle rim lighting
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 4.0) * 0.15;

    // Combine lighting - higher ambient, balanced diffuse
    vec3 ambient = ambientLightColor * 0.55;
    vec3 diffuse = directionalLightColor * diff * 0.35;
    vec3 fill = directionalLightColor * wrap * 0.15; // Fill light
    vec3 rimColor = vec3(1.0, 0.95, 0.9) * rim;

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation based on position for more organic look
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

// Dust particle data
interface DustParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  maxLife: number;
  size: number;
}

export class CavalryRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private cavalryData: CavalryData[] = [];
  private entityMap: Map<Entity, CavalryData> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { x: number; y: number; z: number }) => void) | null = null;

  // Dust particle system
  private dustParticles: DustParticle[] = [];
  private dustGeometry: THREE.BufferGeometry | null = null;
  private dustMaterial: THREE.ShaderMaterial | null = null;
  private dustPoints: THREE.Points | null = null;
  private dustSpawnAccumulator = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.createDustParticleSystem();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for cavalry charge events to trigger attack animation
    this.attackEventHandler = (data: { x: number; y: number; z: number }) => {
      // Find the cavalry unit at this position
      for (const cavalry of this.cavalryData) {
        const dx = cavalry.x - data.x;
        const dz = cavalry.z - data.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < 0.5) {
          cavalry.attackTime = this.time;
          cavalry.attackDuration = 0.65;
          break;
        }
      }
    };
    EventBus.on('cavalry:charge', this.attackEventHandler);
  }

  private createDustParticleSystem(): void {
    // Create geometry with attributes
    this.dustGeometry = new THREE.BufferGeometry();

    const positions = new Float32Array(MAX_DUST_PARTICLES * 3);
    const sizes = new Float32Array(MAX_DUST_PARTICLES);
    const alphas = new Float32Array(MAX_DUST_PARTICLES);

    this.dustGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.dustGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.dustGeometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));

    // Create material
    this.dustMaterial = new THREE.ShaderMaterial({
      vertexShader: dustVertexShader,
      fragmentShader: dustFragmentShader,
      uniforms: {
        uTime: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    // Create points
    this.dustPoints = new THREE.Points(this.dustGeometry, this.dustMaterial);
    this.dustPoints.frustumCulled = false;
    this.scene.add(this.dustPoints);
  }

  private spawnDustParticle(x: number, y: number, z: number, speed: number): void {
    if (this.dustParticles.length >= MAX_DUST_PARTICLES) {
      // Remove oldest particle
      this.dustParticles.shift();
    }

    // Random offset behind the cavalry
    const spreadX = (Math.random() - 0.5) * 0.8;
    const spreadZ = (Math.random() - 0.5) * 0.8;

    this.dustParticles.push({
      x: x + spreadX,
      y: y + 0.1 + Math.random() * 0.2,
      z: z + spreadZ,
      vx: (Math.random() - 0.5) * 0.3,
      vy: 0.5 + Math.random() * 0.8,
      vz: (Math.random() - 0.5) * 0.3,
      life: 0,
      maxLife: 0.8 + Math.random() * 0.6,
      size: 8 + Math.random() * 12 * speed,
    });
  }

  private updateDustParticles(deltaTime: number): void {
    if (!this.dustGeometry || !this.dustMaterial) return;

    // Spawn new particles for moving cavalry
    this.dustSpawnAccumulator += deltaTime;
    const spawnInterval = 0.03; // Spawn every 30ms per cavalry

    while (this.dustSpawnAccumulator >= spawnInterval) {
      this.dustSpawnAccumulator -= spawnInterval;

      for (const cavalry of this.cavalryData) {
        if (cavalry.speed > 0.3) {
          // Only spawn dust when moving fast
          const particleCount = Math.floor(cavalry.speed * 2);
          for (let i = 0; i < particleCount; i++) {
            this.spawnDustParticle(cavalry.x, cavalry.y, cavalry.z, cavalry.speed);
          }
        }
      }
    }

    // Update existing particles
    const positions = this.dustGeometry.getAttribute('position') as THREE.BufferAttribute;
    const sizes = this.dustGeometry.getAttribute('size') as THREE.BufferAttribute;
    const alphas = this.dustGeometry.getAttribute('alpha') as THREE.BufferAttribute;

    let aliveCount = 0;
    for (let i = this.dustParticles.length - 1; i >= 0; i--) {
      const p = this.dustParticles[i];
      p.life += deltaTime;

      if (p.life >= p.maxLife) {
        this.dustParticles.splice(i, 1);
        continue;
      }

      // Update position with physics
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;
      p.z += p.vz * deltaTime;

      // Slow down and rise
      p.vx *= 0.95;
      p.vy *= 0.92;
      p.vz *= 0.95;

      // Calculate alpha based on life
      const lifeRatio = p.life / p.maxLife;
      const alpha = lifeRatio < 0.2 ? lifeRatio * 5 : 1 - (lifeRatio - 0.2) / 0.8;

      // Store in buffers
      positions.setXYZ(aliveCount, p.x, p.y, p.z);
      sizes.setX(aliveCount, p.size * (1 + lifeRatio * 0.5));
      alphas.setX(aliveCount, alpha);

      aliveCount++;
    }

    // Clear remaining buffer slots
    for (let i = aliveCount; i < MAX_DUST_PARTICLES; i++) {
      positions.setXYZ(i, 0, -1000, 0); // Move off-screen
      alphas.setX(i, 0);
    }

    positions.needsUpdate = true;
    sizes.needsUpdate = true;
    alphas.needsUpdate = true;

    // Update draw range
    this.dustGeometry.setDrawRange(0, aliveCount);

    // Update time uniform
    this.dustMaterial.uniforms.uTime.value = this.time;
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createCavalryGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_CAVALRY);
    const phases = new Float32Array(MAX_CAVALRY);
    const attackTimes = new Float32Array(MAX_CAVALRY);
    for (let i = 0; i < MAX_CAVALRY; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2; // Random phase offset
      attackTimes[i] = -1; // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: cavalryVertexShader,
        fragmentShader: cavalryFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
          uGlobalSpeed: { value: 1.0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_CAVALRY);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createCavalryGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // ========== HORSE ==========

    // === Horse Body (main barrel) - slimmer proportions ===
    const bodyMain = new THREE.SphereGeometry(0.16, 12, 10);
    bodyMain.scale(1.5, 0.85, 0.65);
    bodyMain.translate(0, 0.24, 0);
    parts.push(bodyMain);
    colors.push(COLORS.horseBay);
    animGroups.push(ANIM_GROUP.BODY);

    // Chest (front bulk) - slimmer
    const chest = new THREE.SphereGeometry(0.11, 10, 8);
    chest.scale(1.0, 1.0, 0.70);
    chest.translate(0.20, 0.26, 0);
    parts.push(chest);
    colors.push(COLORS.horseBay);
    animGroups.push(ANIM_GROUP.BODY);

    // Hindquarters - slimmer
    const rump = new THREE.SphereGeometry(0.12, 10, 8);
    rump.scale(0.95, 0.90, 0.70);
    rump.translate(-0.16, 0.26, 0);
    parts.push(rump);
    colors.push(COLORS.horseBay);
    animGroups.push(ANIM_GROUP.BODY);

    // === Horse Neck ===
    const neck = new THREE.CylinderGeometry(0.08, 0.10, 0.18, 8);
    neck.rotateZ(-0.6);
    neck.translate(0.32, 0.35, 0);
    parts.push(neck);
    colors.push(COLORS.horseBay);
    animGroups.push(ANIM_GROUP.HEAD);

    // === Horse Head ===
    const head = new THREE.SphereGeometry(0.075, 10, 8);
    head.scale(1.5, 1.1, 0.9);
    head.translate(0.42, 0.44, 0);
    parts.push(head);
    colors.push(COLORS.horseBay);
    animGroups.push(ANIM_GROUP.HEAD);

    // Snout/muzzle
    const snout = new THREE.CylinderGeometry(0.04, 0.05, 0.12, 8);
    snout.rotateZ(Math.PI / 2 + 0.4);
    snout.translate(0.52, 0.41, 0);
    parts.push(snout);
    colors.push(COLORS.horseMuzzle);
    animGroups.push(ANIM_GROUP.HEAD);

    // Nostrils
    for (const z of [-0.025, 0.025]) {
      const nostril = new THREE.SphereGeometry(0.012, 6, 4);
      nostril.translate(0.58, 0.40, z);
      parts.push(nostril);
      colors.push(COLORS.horseDark);
      animGroups.push(ANIM_GROUP.HEAD);
    }

    // Ears
    for (const z of [-0.04, 0.04]) {
      const ear = new THREE.ConeGeometry(0.022, 0.055, 6);
      ear.rotateX(z > 0 ? -0.25 : 0.25);
      ear.rotateZ(-0.2);
      ear.translate(0.40, 0.52, z);
      parts.push(ear);
      colors.push(COLORS.horseBay);
      animGroups.push(ANIM_GROUP.HEAD);
    }

    // Eyes
    for (const z of [-0.045, 0.045]) {
      const eye = new THREE.SphereGeometry(0.015, 6, 4);
      eye.translate(0.44, 0.46, z);
      parts.push(eye);
      colors.push(COLORS.horseDark);
      animGroups.push(ANIM_GROUP.HEAD);
    }

    // === Horse Mane ===
    // Multiple segments for flowing effect
    for (let i = 0; i < 6; i++) {
      const maneSegment = new THREE.BoxGeometry(0.04, 0.06 - i * 0.005, 0.025);
      maneSegment.translate(0.30 - i * 0.04, 0.42 + i * 0.01, 0);
      parts.push(maneSegment);
      colors.push(COLORS.mane);
      animGroups.push(ANIM_GROUP.MANE);
    }
    // Forelock (between ears)
    const forelock = new THREE.BoxGeometry(0.03, 0.05, 0.03);
    forelock.translate(0.38, 0.50, 0);
    parts.push(forelock);
    colors.push(COLORS.mane);
    animGroups.push(ANIM_GROUP.MANE);

    // === Horse Legs === - narrower stance for slimmer horse
    this.createHorseLeg(parts, colors, animGroups, 0.16, 0.055, ANIM_GROUP.FRONT_LEFT_LEG, true);
    this.createHorseLeg(parts, colors, animGroups, 0.16, -0.055, ANIM_GROUP.FRONT_RIGHT_LEG, true);
    this.createHorseLeg(parts, colors, animGroups, -0.14, 0.055, ANIM_GROUP.BACK_LEFT_LEG, false);
    this.createHorseLeg(parts, colors, animGroups, -0.14, -0.055, ANIM_GROUP.BACK_RIGHT_LEG, false);

    // === Horse Tail ===
    // Single tapered tail piece - connected to rump at (-0.16 - 0.11 = -0.27)
    const tail = new THREE.CylinderGeometry(0.008, 0.025, 0.18, 6);
    tail.rotateZ(0.5); // Angle backward-upward
    tail.translate(-0.26, 0.28, 0);
    parts.push(tail);
    colors.push(COLORS.mane);
    animGroups.push(ANIM_GROUP.TAIL);

    // === Saddle === adjusted for slimmer horse
    const saddleBase = new THREE.BoxGeometry(0.15, 0.03, 0.12);
    saddleBase.translate(0, 0.34, 0);
    parts.push(saddleBase);
    colors.push(COLORS.woodDark);
    animGroups.push(ANIM_GROUP.BODY);

    // Saddle horn (front)
    const saddleHorn = new THREE.CylinderGeometry(0.018, 0.022, 0.035, 6);
    saddleHorn.translate(0.06, 0.38, 0);
    parts.push(saddleHorn);
    colors.push(COLORS.woodDark);
    animGroups.push(ANIM_GROUP.BODY);

    // Saddle cantle (back)
    const saddleCantle = new THREE.BoxGeometry(0.035, 0.045, 0.10);
    saddleCantle.rotateX(0.1);
    saddleCantle.translate(-0.06, 0.38, 0);
    parts.push(saddleCantle);
    colors.push(COLORS.woodDark);
    animGroups.push(ANIM_GROUP.BODY);

    // Saddle blanket (faction color) - adjusted for slimmer horse
    const blanket = new THREE.BoxGeometry(0.18, 0.015, 0.14);
    blanket.translate(0, 0.32, 0);
    parts.push(blanket);
    colors.push(COLORS.markerPrimary); // Primary faction color
    animGroups.push(ANIM_GROUP.BODY);

    // Stirrups - adjusted for slimmer horse
    for (const z of [-0.08, 0.08]) {
      const stirrup = new THREE.TorusGeometry(0.025, 0.006, 6, 10);
      stirrup.translate(0, 0.16, z);
      parts.push(stirrup);
      colors.push(COLORS.bronze);
      animGroups.push(ANIM_GROUP.STATIC);

      // Stirrup leather
      const leather = new THREE.BoxGeometry(0.01, 0.10, 0.012);
      leather.translate(0, 0.21, z);
      parts.push(leather);
      colors.push(COLORS.cavalryAccent);
      animGroups.push(ANIM_GROUP.STATIC);
    }

    // Reins
    const rein = new THREE.CylinderGeometry(0.006, 0.006, 0.25, 4);
    rein.rotateZ(0.5);
    rein.translate(0.20, 0.38, 0);
    parts.push(rein);
    colors.push(COLORS.cavalryAccent);
    animGroups.push(ANIM_GROUP.HEAD);

    // ========== RIDER ==========
    const riderScale = 0.78;
    const riderBaseY = 0.38;

    // === Rider Body ===
    // Torso (armored)
    const torso = new THREE.CylinderGeometry(0.06 * riderScale, 0.07 * riderScale, 0.12 * riderScale, 8);
    torso.translate(0, riderBaseY + 0.12, 0);
    parts.push(torso);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    // Shoulder armor
    for (const z of [-0.055, 0.055]) {
      const shoulder = new THREE.SphereGeometry(0.035 * riderScale, 6, 5);
      shoulder.scale(1.2, 0.8, 1.0);
      shoulder.translate(0, riderBaseY + 0.18, z);
      parts.push(shoulder);
      colors.push(COLORS.cavalryArmor);
      animGroups.push(ANIM_GROUP.RIDER_BODY);
    }

    // Belt
    const belt = new THREE.CylinderGeometry(0.072 * riderScale, 0.072 * riderScale, 0.02, 8);
    belt.translate(0, riderBaseY + 0.06, 0);
    parts.push(belt);
    colors.push(COLORS.cavalryAccent);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    // === Rider Head ===
    const riderHead = new THREE.SphereGeometry(0.07 * riderScale, 10, 8);
    riderHead.translate(0, riderBaseY + 0.26, 0);
    parts.push(riderHead);
    colors.push(COLORS.skin);
    animGroups.push(ANIM_GROUP.RIDER_HEAD);

    // Chinese-style helmet with distinct shape
    // Main dome
    const helmetDome = new THREE.SphereGeometry(0.075 * riderScale, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.6);
    helmetDome.translate(0, riderBaseY + 0.29, 0);
    parts.push(helmetDome);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_HEAD);

    // Helmet crest/spike
    const helmetSpike = new THREE.ConeGeometry(0.018 * riderScale, 0.05 * riderScale, 6);
    helmetSpike.translate(0, riderBaseY + 0.36, 0);
    parts.push(helmetSpike);
    colors.push(COLORS.gold);
    animGroups.push(ANIM_GROUP.RIDER_HEAD);

    // Helmet cheek guards
    for (const z of [-0.04, 0.04]) {
      const cheekGuard = new THREE.BoxGeometry(0.02, 0.04, 0.025);
      cheekGuard.translate(0.02, riderBaseY + 0.24, z * riderScale);
      parts.push(cheekGuard);
      colors.push(COLORS.cavalryArmor);
      animGroups.push(ANIM_GROUP.RIDER_HEAD);
    }

    // Helmet plume/tassel
    const plume = new THREE.ConeGeometry(0.02, 0.06, 6);
    plume.rotateX(-0.5);
    plume.translate(-0.02, riderBaseY + 0.35, 0);
    parts.push(plume);
    colors.push(COLORS.tassel);
    animGroups.push(ANIM_GROUP.RIDER_HEAD);

    // === Rider Arms ===
    // Left arm (holding reins)
    const leftUpperArm = new THREE.CylinderGeometry(0.018, 0.016, 0.06, 6);
    leftUpperArm.rotateZ(0.6);
    leftUpperArm.translate(-0.04, riderBaseY + 0.14, 0.04);
    parts.push(leftUpperArm);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    const leftForearm = new THREE.CylinderGeometry(0.015, 0.013, 0.05, 6);
    leftForearm.rotateZ(1.2);
    leftForearm.translate(-0.06, riderBaseY + 0.10, 0.04);
    parts.push(leftForearm);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    const leftHand = new THREE.SphereGeometry(0.014, 5, 4);
    leftHand.translate(-0.06, riderBaseY + 0.07, 0.04);
    parts.push(leftHand);
    colors.push(COLORS.skin);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    // Right arm (holding lance)
    const rightUpperArm = new THREE.CylinderGeometry(0.018, 0.016, 0.06, 6);
    rightUpperArm.rotateZ(-0.7);
    rightUpperArm.translate(0.04, riderBaseY + 0.15, -0.01);
    parts.push(rightUpperArm);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    const rightForearm = new THREE.CylinderGeometry(0.015, 0.013, 0.055, 6);
    rightForearm.rotateZ(-0.4);
    rightForearm.translate(0.08, riderBaseY + 0.18, -0.01);
    parts.push(rightForearm);
    colors.push(COLORS.cavalryArmor);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    const rightHand = new THREE.SphereGeometry(0.015, 5, 4);
    rightHand.translate(0.10, riderBaseY + 0.20, -0.01);
    parts.push(rightHand);
    colors.push(COLORS.skin);
    animGroups.push(ANIM_GROUP.RIDER_BODY);

    // === Rider Legs (bent for riding) === - adjusted for slimmer horse
    for (const z of [-0.05, 0.05]) {
      // Upper leg (thigh)
      const thigh = new THREE.CylinderGeometry(0.022, 0.020, 0.08, 6);
      thigh.rotateX(z > 0 ? 0.25 : -0.25);
      thigh.rotateZ(0.2);
      thigh.translate(-0.01, riderBaseY + 0.02, z);
      parts.push(thigh);
      colors.push(COLORS.cavalryArmor);
      animGroups.push(ANIM_GROUP.RIDER_BODY);

      // Lower leg (shin)
      const shin = new THREE.CylinderGeometry(0.016, 0.013, 0.07, 6);
      shin.rotateX(z > 0 ? -0.35 : 0.35);
      shin.translate(0.02, riderBaseY - 0.05, z * 1.2);
      parts.push(shin);
      colors.push(COLORS.cavalryArmor);
      animGroups.push(ANIM_GROUP.RIDER_BODY);

      // Boot
      const boot = new THREE.BoxGeometry(0.032, 0.022, 0.022);
      boot.translate(0.03, riderBaseY - 0.09, z * 1.3);
      parts.push(boot);
      colors.push(COLORS.cavalryAccent);
      animGroups.push(ANIM_GROUP.RIDER_BODY);
    }


    // === WEAPON: Lance (骑枪) ===
    // Lance angle and shaft parameters
    const lanceAngle = -0.3; // Tilt angle
    const shaftLength = 0.75;
    const shaftCenterY = riderBaseY + 0.35;
    const shaftCenterX = 0.18;

    // Calculate shaft top position (for connecting head)
    const shaftTopOffsetX = (shaftLength / 2) * Math.sin(-lanceAngle);
    const shaftTopOffsetY = (shaftLength / 2) * Math.cos(-lanceAngle);
    const shaftTopX = shaftCenterX + shaftTopOffsetX;
    const shaftTopY = shaftCenterY + shaftTopOffsetY;

    // Lance shaft
    const lanceShaft = new THREE.CylinderGeometry(0.010, 0.016, shaftLength, 6);
    lanceShaft.rotateZ(lanceAngle);
    lanceShaft.translate(shaftCenterX, shaftCenterY, 0);
    parts.push(lanceShaft);
    colors.push(COLORS.wood);
    animGroups.push(ANIM_GROUP.LANCE);

    // Lance head - positioned at shaft top
    const headLength = 0.10;
    const headCenterX = shaftTopX + (headLength / 2) * Math.sin(-lanceAngle);
    const headCenterY = shaftTopY + (headLength / 2) * Math.cos(-lanceAngle);
    const lanceHead = new THREE.ConeGeometry(0.025, headLength, 4);
    lanceHead.rotateZ(lanceAngle);
    lanceHead.translate(headCenterX, headCenterY, 0);
    parts.push(lanceHead);
    colors.push(COLORS.steel);
    animGroups.push(ANIM_GROUP.LANCE);

    // Lance guard - near the grip point
    const guardY = shaftCenterY - shaftTopOffsetY * 0.3;
    const guardX = shaftCenterX - shaftTopOffsetX * 0.3;
    const lanceGuard = new THREE.TorusGeometry(0.022, 0.006, 6, 8);
    lanceGuard.rotateY(Math.PI / 2);
    lanceGuard.rotateZ(lanceAngle);
    lanceGuard.translate(guardX, guardY, 0);
    parts.push(lanceGuard);
    colors.push(COLORS.bronze);
    animGroups.push(ANIM_GROUP.LANCE);

    // Lance tassel - just below the head
    const tasselY = shaftTopY - 0.05;
    const tasselX = shaftTopX - 0.02;
    const tassel1 = new THREE.BoxGeometry(0.006, 0.05, 0.035);
    tassel1.rotateZ(lanceAngle * 0.5);
    tassel1.translate(tasselX, tasselY, 0.01);
    parts.push(tassel1);
    colors.push(COLORS.tassel);
    animGroups.push(ANIM_GROUP.LANCE);

    // Merge all geometries with animation group data
    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private createHorseLeg(
    parts: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[],
    x: number,
    z: number,
    animGroup: number,
    isFront: boolean
  ): void {
    const legLength = isFront ? 0.11 : 0.10;
    const lowerLength = isFront ? 0.10 : 0.09;

    // Upper leg - slimmer
    const upper = new THREE.CylinderGeometry(0.026, 0.022, legLength, 6);
    upper.translate(x, 0.14, z);
    parts.push(upper);
    colors.push(COLORS.horseBay);
    animGroups.push(animGroup);

    // Knee joint - slimmer
    const knee = new THREE.SphereGeometry(0.020, 6, 4);
    knee.translate(x, 0.09, z);
    parts.push(knee);
    colors.push(COLORS.horseBay);
    animGroups.push(animGroup);

    // Lower leg - slimmer
    const lower = new THREE.CylinderGeometry(0.018, 0.014, lowerLength, 6);
    lower.translate(x, 0.04, z);
    parts.push(lower);
    colors.push(COLORS.horseBay);
    animGroups.push(animGroup);

    // Fetlock (ankle) - slimmer
    const fetlock = new THREE.SphereGeometry(0.014, 5, 4);
    fetlock.translate(x, -0.01, z);
    parts.push(fetlock);
    colors.push(COLORS.horseBay);
    animGroups.push(animGroup);

    // Hoof - slimmer
    const hoof = new THREE.CylinderGeometry(0.018, 0.022, 0.022, 8);
    hoof.translate(x, -0.025, z);
    parts.push(hoof);
    colors.push(COLORS.hoof);
    animGroups.push(animGroup);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addCavalry(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    if (this.entityMap.has(entity)) return;

    const data: CavalryData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.65,
    };
    this.cavalryData.push(data);
    this.entityMap.set(entity, data);
  }

  removeCavalry(entity: Entity): void {
    const cavalry = this.entityMap.get(entity);
    if (!cavalry) return;

    const index = this.cavalryData.indexOf(cavalry);
    if (index !== -1) {
      const last = this.cavalryData[this.cavalryData.length - 1];
      this.cavalryData[index] = last;
      this.cavalryData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateCavalry(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const cavalry = this.entityMap.get(entity);
    if (!cavalry) return;

    // Calculate speed from movement
    const dx = x - cavalry.x;
    const dz = z - cavalry.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0); // Scale movement to 0-1
    cavalry.speed = cavalry.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      cavalry.rotation = rotation;
    } else if (distance > 0.001) {
      cavalry.rotation = Math.atan2(dx, dz);
    }

    cavalry.x = x;
    cavalry.y = y;
    cavalry.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group cavalry by faction for instanced rendering
    const factionGroups = new Map<FactionId, CavalryData[]>();
    for (const cavalry of this.cavalryData) {
      if (!factionGroups.has(cavalry.factionId)) {
        factionGroups.set(cavalry.factionId, []);
      }
      factionGroups.get(cavalry.factionId)!.push(cavalry);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const cavalryList = factionGroups.get(factionId) || [];
      mesh.count = cavalryList.length;

      if (cavalryList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < cavalryList.length; i++) {
        const cavalry = cavalryList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, cavalry.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, cavalry.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, cavalry.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(cavalry.x, cavalry.y + surfaceOffset, cavalry.z);
        // Model faces +X, but rotation assumes +Z is forward, so offset by -PI/2
        this.dummy.rotation.set(0, cavalry.rotation - Math.PI / 2, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }

    // Update dust particles
    this.updateDustParticles(deltaTime);
  }

  getCavalryCount(): number {
    return this.cavalryData.length;
  }

  clear(): void {
    this.cavalryData.length = 0;
    this.dustParticles.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('cavalry:charge', this.attackEventHandler);
    }

    // Dispose cavalry meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.cavalryData.length = 0;

    // Dispose dust particle system
    if (this.dustPoints) {
      this.scene.remove(this.dustPoints);
    }
    if (this.dustGeometry) {
      this.dustGeometry.dispose();
    }
    if (this.dustMaterial) {
      this.dustMaterial.dispose();
    }
    this.dustParticles.length = 0;
  }
}
